import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  Snackbar,
  Button
} from '@mui/material';
import Charts from '../charts/Charts';
import ChartSettings from '../chartSettings/ChartSettings';
import MobileCuesHeader from './MobileCuesHeader';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoute } from '../charts/ChartsSlice';
import { useUserLocation } from '../../context/LocationContext';
import { cacheChartSettings } from '../chartSettings/ChartSettingsSlice';
import { calcRouteChunks } from '../charts/ChartsSlice';
// Move media query outside component to prevent re-renders
const isMobileQuery = '(max-width:600px)';



function Cues() {
  const { routeId, source } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [locationSnackbarOpen, setLocationSnackbarOpen] = useState(false);
  const [locationErrorSnackbarOpen, setLocationErrorSnackbarOpen] = useState(false);
  const isMobile = useMediaQuery(isMobileQuery);
  const routeData = useSelector(state => state.charts.route_json);
  const chartSettings = useSelector(state => state.chartSettings.chartSettings)
  const chartData = useSelector(state => state.charts.chartData);
  const { getCurrentLocation } = useUserLocation();
  const userLocation = useSelector(state => state.charts.userLocation);
  const showUserLocation = useSelector(state => state.chartSettings.chartSettings.showUserLocation);
  const gpxLoading = useSelector(state => state.charts.gpxLoading);
  const gpxError = useSelector(state => state.charts.gpxError);
  // Get dispatch function
  const dispatch = useDispatch();

  // Handle drawer state based on screen size
  useEffect(() => {
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  // Load route data
  useEffect(() => {
    if (!routeId) return;

    let isMounted = true;

    const loadRouteData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the fetchRoute thunk which handles both online and offline cases
        await dispatch(fetchRoute({ routeId, source })).unwrap();
        // navigator.geolocation.getCurrentPosition((e) => console.log(e))
        if (!isMounted) return;
      } catch (err) {
        console.error('Error loading route:', err);
        if (isMounted) {
          setError('Failed to load route data');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRouteData();

    return () => {
      isMounted = false;
    };
  }, [routeId, source, dispatch]);

  const handleCacheChartSettings = (routeId, chartSettings) => {
    dispatch(cacheChartSettings({ routeId, chartSettings }));
  };

  const handleLocationClick = () => {
    if (!showUserLocation) {
      setLocationSnackbarOpen(true);
      return;
    }
    getCurrentLocation().catch((err) => {
      if (err.code === 1 || err.message?.includes('denied') || err.message?.includes('Permission')) {
        setLocationErrorSnackbarOpen(true);
      }
    });
  };

  const handleSave = (e) => {
    e.stopPropagation();
    if (isMobile) {
      setDrawerOpen(false);
    }
    // Calculate route chunks before saving
    dispatch(calcRouteChunks({ chartSettings, trackPointsWithPoi: chartData }));
    if (routeData.isCached) {
      handleCacheChartSettings(routeData.id, chartSettings);
    }
  };


  if (loading || gpxLoading) {
    return (
      <Box sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {gpxLoading ? 'Loading elevation data...' : 'Loading route...'}
        </Typography>
      </Box>
    );
  }

  if (error || gpxError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error || gpxError}</Typography>
        {gpxError && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This may be due to network issues or the route not having elevation data available.
          </Typography>
        )}
      </Box>
    );
  }
  return (
    <Box className="print-overflow-auto print-all-charts" sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative'
    }}>
      {/* Header */}
      <MobileCuesHeader
        routeData={routeData}
        isMobile={isMobile}
        onBackClick={() => window.history.back()}
        onSettingsClick={() => setDrawerOpen(!drawerOpen)}
        onLocationClick={handleLocationClick}
        userLocation={userLocation}
        chartSettings={chartSettings}
      />

      {/* Main content area */}
      <Box className="print-overflow-auto print-all-charts" sx={{
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        height: 'calc(100% - 80px)'
      }}>
        {/* Configuration Panel - Desktop only */}
        {!isMobile && drawerOpen && (
          <Box className="no-print" sx={{
            width: 400,
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'auto',
            flexShrink: 0
          }}>
            <Box sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0
            }}>
              <Typography variant="h6">Route Configuration</Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
              >
                {routeData.isCached ? "Save" : "Apply"}
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <ChartSettings />
            </Box>
          </Box>
        )}

        {/* Chart content area */}
        <Box className="print-overflow-auto print-all-charts" sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          // overflow: 'auto',
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}>
          <Charts />
          <br />
        </Box>
      </Box>


      <Snackbar
        open={locationSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setLocationSnackbarOpen(false)}
        message="Please enable 'Show User Location' in settings to use location tracking"
        action={
          <Button color="primary" size="small" onClick={() => navigate('/settings')}>
            Settings
          </Button>
        }
      />
      <Snackbar
        open={locationErrorSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setLocationErrorSnackbarOpen(false)}
        message="Location permission denied. Please enable location access in your browser settings."
      />
    </Box >
  );
}

export default Cues; 